import { Bars3Icon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyTypeDefinition, SystemPropertySummary, TemplatePropertySummary } from '../../data/types';

interface PropertyNavigatorProps {
  properties: TemplatePropertySummary[];
  types: PropertyTypeDefinition[];
  systemProperties: SystemPropertySummary[];
  move: (from: number, to: number) => void;
  onJump: (tokenName: string) => void;
}

/** Desktop-only quick nav: drag to reorder, click a row to jump to that field. */
export function PropertyNavigator({ properties, types, systemProperties, move, onJump }: PropertyNavigatorProps) {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  return (
    <nav aria-label={t('properties.nav.title')} className="rounded-lg border border-border bg-surface p-3">
      <p className="text-sm font-medium text-foreground">{t('properties.nav.title')}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t('properties.nav.hint')}</p>

      <ol className="mt-3 grid gap-1">
        {properties.map((property, index) => {
          const name = property.displayName || property.tokenName;
          const unregistered = !types.find((d) => d.id === property.propertyType);
          const inheritedAdvanced = systemProperties.find((s) => s.tokenName === property.tokenName)?.isAdvanced ?? false;
          const isAdvanced = property.isAdvanced ?? inheritedAdvanced;

          return (
            <li
              key={property.tokenName}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => {
                if (dragIndex !== null) move(dragIndex, index);
                setDragIndex(null);
              }}
              className="flex items-center gap-2 rounded-sm"
            >
              <Bars3Icon className="size-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden="true" />
              <button
                type="button"
                onClick={() => onJump(property.tokenName)}
                aria-label={t('properties.nav.jump', { name })}
                className="flex min-w-0 flex-1 items-center gap-1.5 rounded-sm px-2 py-1 text-left text-sm text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {property.isRequired ? (
                  <span className="shrink-0 text-sm leading-none font-semibold text-destructive" title={t('properties.required')}>
                    *
                  </span>
                ) : null}
                {isAdvanced ? (
                  <span
                    className="grid size-4 shrink-0 place-items-center rounded-full bg-accent text-[10px] font-semibold text-accent-foreground"
                    title={t('properties.advanced')}
                  >
                    {t('properties.nav.advancedBadge')}
                  </span>
                ) : null}
              </button>
              {unregistered ? (
                <span title={t('properties.nav.unregistered', { name })}>
                  <ExclamationTriangleIcon className="size-4 shrink-0 text-destructive" aria-hidden="true" />
                  <span className="sr-only">{t('properties.nav.unregistered', { name })}</span>
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
